/**
 * FeedbackInput Component
 * Text input with character counter for menu feedback
 * Max 5000 characters with visual warnings as user approaches limit
 */

import { useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const MAX_CHARACTERS = 5000;

interface FeedbackInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Get character count color class based on usage percentage
 */
function getCharacterCountClass(count: number, max: number): string {
  if (count >= max) return 'text-destructive font-medium';
  if (count > max * 0.8) return 'text-amber-600';
  return 'text-muted-foreground';
}

/**
 * FeedbackInput - Textarea with character counter
 *
 * Features:
 * - Character counter showing current/max characters
 * - Warning color at 80% capacity (amber)
 * - Error color at 100% capacity (red)
 * - Enforces max character limit
 * - Disabled state for Sundays/announcements
 */
export function FeedbackInput({
  value,
  onChange,
  disabled = false,
  placeholder = 'Add feedback (optional)...',
  className,
}: FeedbackInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= MAX_CHARACTERS) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <div className={cn('relative', className)}>
      <Textarea
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={disabled ? 'N/A' : placeholder}
        className={cn(
          'min-h-[60px] resize-none text-sm pr-16 bg-white',
          disabled && 'bg-muted cursor-not-allowed opacity-60'
        )}
        maxLength={MAX_CHARACTERS}
      />
      {!disabled && (
        <div
          className={cn(
            'absolute bottom-2 right-2 text-xs select-none pointer-events-none',
            getCharacterCountClass(value.length, MAX_CHARACTERS)
          )}
        >
          {value.length}/{MAX_CHARACTERS}
        </div>
      )}
    </div>
  );
}
